import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { t } from "i18next";
import { isEmpty } from "lodash";
import moment from "moment";
import { FcCalendar } from "react-icons/fc";
import { useDispatch, useSelector } from "react-redux";
import { useOutletContext } from "react-router";
import LocationPinIcon from "src/images/icons/location-pin.svg";
import TimeIcon from "src/images/icons/time.svg";

import { BOOKING_CONSTANTS } from "@constants/Bookings";
import { DRAWER_HEIGHT, DrawerTimeSlotProps } from "@constants/Drawer";
import { BOOKING_DRAWER_STEPS } from "@enums/component.enum";
import { DateFormats } from "@enums/shared.enum";
import { useBooking } from "@hooks/useBooking/useBooking";
import { useBookingSubmit } from "@hooks/useBookingSubmit/useBookingSubmit";
import { usePricing } from "@hooks/usePricing/usePricing";
import { ResourceType } from "@models/services/booking";
import { getLocationType, locationType } from "@models/services/organisation";
import { applyLocalization } from "@services/common.service";
import bookingCalendarSlice from "@slices/bookingCalendar.slice";
import drawerSlice from "@slices/drawer.slice";
import { RootState } from "@store/index";
import { buildTempEvent } from "@utils/booking.utils";

import BookingDrawerStepOne from "../BookingDrawer/BookingDrawerStepOne/BookingDrawerStepOne";
import BookingDrawerStepTwo from "../BookingDrawer/BookingDrawerStepTwo/BookingDrawerStepTwo";
import Terms from "../BookingForm/Terms/Terms";

const DrawerTimeSlot = ({
  bookingSlots,
  isBookingDisable,
  priceLoader,
}: DrawerTimeSlotProps) => {
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [currentStep, setCurrentStep] = useState<BOOKING_DRAWER_STEPS>(
    BOOKING_DRAWER_STEPS.ONE
  );
  const [guestError, setGuestError] = useState("");

  const dispatch = useDispatch();

  const {
    gdprAccepted,
    guestList,
    legalTermsAccepted,
    paymentLoader,
    resourcePrices,
    selectedDate,
    selectedSlot,
    selectedTime,
  } = useSelector((State: RootState) => State.booking);

  const openPercent = useSelector(
    (state: RootState) => state.drawer.openPercent
  );

  const drawerBookingData = useSelector(
    (State: RootState) => State.drawer.drawerBookingData
  );
  const showBookingDrawer = useSelector(
    (State: RootState) => State.drawer.showBookingDrawer
  );
  const { resourceId } = drawerBookingData;

  const context: [getLocationType, locationType] = useOutletContext();
  const { getSelectedResource } = useBooking();

  const drawerContainerRef = useRef<HTMLDivElement>(null);

  const { getPriceByResourceSlot } = usePricing();

  const hasValidResource = resourceId !== undefined;

  const { guestPriceAmount, priceForResource, totalPriceWithTax } =
    useMemo(() => {
      const {
        guestPrice = 0,
        price = 0,
        totalPriceWithTax = 0,
      } = getPriceByResourceSlot(
        resourcePrices,
        resourceId,
        selectedSlot?.slot?.slot
      ) || {};

      const numPrice = Number(price);
      const numGuestPrice = Number(guestPrice);

      return {
        guestPriceAmount: Number.isNaN(numGuestPrice) ? 0 : numGuestPrice,
        priceForResource: Number.isNaN(numPrice) ? 0 : numPrice,
        totalPriceWithTax,
      };
    }, [
      resourcePrices,
      resourceId,
      selectedSlot?.slot?.slot,
      getPriceByResourceSlot,
    ]);

  useEffect(() => {
    setGuestError("");
  }, [guestList, selectedSlot]);

  useEffect(() => {
    setCurrentStep(BOOKING_DRAWER_STEPS.ONE);
  }, [showBookingDrawer]);

  const isPriceFree = priceForResource === 0;

  const canProceedToNextStep = Boolean(
    selectedTime && selectedSlot?.resourceId
  );

  const currentResource: ResourceType = useMemo(
    () => getSelectedResource(bookingSlots, resourceId),
    [bookingSlots, resourceId]
  );

  const allowBringGuest = useMemo(
    () =>
      Boolean(
        currentResource?.policyInfo?.allowBringGuest &&
          currentResource?.slotInfo?.slots?.allowedGuestTimings.length > 0
      ),
    [currentResource]
  );

  const currency = resourcePrices?.currency || "";

  const { handleBookingSubmit } = useBookingSubmit({
    selectedPaymentMethod,
    setGuestError,
    totalPriceWithTax,
  });

  const handleBack = () => {
    if (currentStep === BOOKING_DRAWER_STEPS.TWO) {
      setCurrentStep(BOOKING_DRAWER_STEPS.ONE);
      scrollToTop();
    } else if (currentStep === BOOKING_DRAWER_STEPS.ONE) {
      dispatch(
        drawerSlice.actions.setOpenPercent(DRAWER_HEIGHT.SECONDARY_POSITION)
      );
    }
  };

  const scrollToTop = useCallback(() => {
    drawerContainerRef.current?.scrollTo({ behavior: "smooth", top: 0 });
  }, []);

  const handleNext = useCallback(() => {
    if (!canProceedToNextStep) {
      return;
    }

    if (currentStep === BOOKING_DRAWER_STEPS.ONE) {
      const shouldGoToStepTwo = !isPriceFree || allowBringGuest;

      if (shouldGoToStepTwo) {
        setCurrentStep(BOOKING_DRAWER_STEPS.TWO);
        scrollToTop();
      } else {
        handleBookingSubmit();
      }
      return;
    }

    handleBookingSubmit();
  }, [
    canProceedToNextStep,
    currentStep,
    isPriceFree,
    allowBringGuest,
    scrollToTop,
    handleBookingSubmit,
  ]);

  const bookingUrls = {
    gdpr: context[1]?.gdprUrl,
    gdprTerms: context[1]?.gdprTerms,
    legal_terms: context[1]?.termsUrl,
    termsService: context[1]?.termsService,
  };

  const canSkipStepTwo =
    !allowBringGuest &&
    isPriceFree &&
    !isEmpty(selectedSlot) &&
    !isEmpty(selectedTime);

  const showTerms = canSkipStepTwo || currentStep === BOOKING_DRAWER_STEPS.TWO;

  const isNextButtonDisabled = useMemo(() => {
    if (!canProceedToNextStep) {
      return true;
    }

    if (isEmpty(selectedSlot) || selectedSlot?.isBookable === false) {
      return true;
    }

    if (currentStep === BOOKING_DRAWER_STEPS.TWO || canSkipStepTwo) {
      if (!gdprAccepted || !legalTermsAccepted) {
        return true;
      }

      const hasPrice = priceForResource + guestPriceAmount > 0;
      if (hasPrice && !selectedPaymentMethod) {
        return true;
      }

      if (paymentLoader || priceLoader) {
        return true;
      }
    }

    return false;
  }, [
    canProceedToNextStep,
    currentStep,
    canSkipStepTwo,
    gdprAccepted,
    legalTermsAccepted,
    priceForResource,
    guestPriceAmount,
    selectedPaymentMethod,
    paymentLoader,
    selectedSlot,
    priceLoader,
  ]);

  const startEndTimeString = useMemo(() => {
    if (!selectedTime) return "";

    const startMoment = moment(selectedTime, DateFormats.Time);
    const endTimeVal = moment(selectedTime, DateFormats.Time).add(
      drawerBookingData?.slot || 0,
      "minutes"
    );

    const startTime = startMoment.format(DateFormats.Time);

    if (!endTimeVal.isValid()) {
      return startTime;
    }

    const endTime = endTimeVal.format(DateFormats.Time);

    return `${startTime} - ${endTime}`;
  }, [selectedTime, drawerBookingData?.slot]);

  const showTimeSection = useMemo(
    () => openPercent > DRAWER_HEIGHT.TWENTY_FIVE_PERCENT,
    [openPercent]
  );

  const buildAndSetTempEvent = useCallback(() => {
    if (!selectedTime || !drawerBookingData?.resourceId) return;

    const selectedTimeSlot =
      selectedSlot?.slot?.slot || BOOKING_CONSTANTS.INTERVAL_15_MINUTES;

    const startDate = moment(
      selectedDate + "T" + selectedTime,
      DateFormats.YMDTime
    ).toDate();
    const endDate = moment(
      selectedDate + "T" + selectedTime,
      DateFormats.YMDTime
    )
      .add(selectedTimeSlot, "minutes")
      .toDate();

    const tempEvent = buildTempEvent({
      end: endDate,
      resourceId: drawerBookingData?.resourceId,
      start: startDate,
    });

    dispatch(
      drawerSlice.actions.setDrawerBookingData({
        ...drawerBookingData,
        slot: selectedTimeSlot,
      })
    );

    dispatch(bookingCalendarSlice.actions.setTempEvents([tempEvent]));
  }, [
    selectedTime,
    selectedSlot?.slot?.slot,
    drawerBookingData?.resourceId,
    showTimeSection,
    dispatch,
    selectedDate,
  ]);

  useEffect(() => {
    buildAndSetTempEvent();
  }, [buildAndSetTempEvent]);

  return (
    <div className="d-flex flex-column h-100" ref={drawerContainerRef}>
      <div className="flex-grow-1 overflow-auto">
        <div className="container">
          {!showTimeSection && (
            <div className="d-flex fs-6 fw-bold gap-1 flex-wrap">
              <div className="d-flex align-items-center gap-1 p-1 rounded">
                <FcCalendar size={20} className="text-light-blue fw-bold" />
                <span>{moment(selectedDate).format("DD.MM.YY")}</span>
              </div>
              <div className="d-flex align-items-center gap-1 p-1 rounded">
                <img
                  src={TimeIcon}
                  alt="Time Icon"
                  className="fg-drawer-icon"
                />
                <span>{startEndTimeString}</span>
              </div>

              <div className="d-flex align-items-center gap-1 p-1 rounded">
                <img
                  src={LocationPinIcon}
                  alt="Resource Location Icon"
                  className="fg-drawer-icon"
                />
                <span>{applyLocalization(currentResource?.title)}</span>
              </div>
            </div>
          )}

          {currentStep === BOOKING_DRAWER_STEPS.ONE && showTimeSection && (
            <BookingDrawerStepOne
              isBookingDisable={isBookingDisable}
              priceLoader={priceLoader}
              hasValidResource={hasValidResource}
              resource={currentResource}
              priceForResource={priceForResource}
              currency={currency}
            />
          )}

          {currentStep === BOOKING_DRAWER_STEPS.TWO && showTimeSection && (
            <BookingDrawerStepTwo
              payment={{
                selectedPaymentMethod: selectedPaymentMethod,
                setSelectedPaymentMethod: setSelectedPaymentMethod,
              }}
              bookingSlots={bookingSlots}
              guestError={guestError}
              allowBringGuest={allowBringGuest}
            />
          )}

          {showTerms && showTimeSection && (
            <div className="mt-4">
              <Terms
                gdpr={bookingUrls?.gdpr}
                legal_terms={bookingUrls?.legal_terms}
                gdprTerms={bookingUrls?.gdprTerms}
                termsService={bookingUrls?.termsService}
              />
            </div>
          )}
        </div>
      </div>
      {showTimeSection && (
        <div className="fg-drawer-footer z-3 border-top pt-2 mt-2 position-sticky bottom-0 py-2">
          <div className="container d-flex justify-content-between">
            <button
              data-testid={"drawer-back-button"}
              key={currentStep}
              className="btn fg-btn btn-default mb-0"
              onClick={handleBack}
            >
              {t("back")}
            </button>
            <button
              data-testid={"drawer-next-button"}
              key={
                canSkipStepTwo || currentStep === BOOKING_DRAWER_STEPS.TWO
                  ? "confirm"
                  : "next"
              }
              disabled={isNextButtonDisabled}
              className="btn fg-btn fg-btn-filled-darkblue mb-0"
              onClick={handleNext}
            >
              {canSkipStepTwo || currentStep === BOOKING_DRAWER_STEPS.TWO
                ? t("ConfirmBooking")
                : t("Next")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrawerTimeSlot;
